// Mirrors backend/src/config/roleScopes.js — keep in sync.

export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  TEAM_MEMBER: 'team_member',
};

export const ROLE_LABELS = {
  admin: 'Admin',
  manager: 'Team Leader',
  team_member: 'Team Member',
};

export const PERMISSIONS = {
  MANAGE_DEPARTMENTS: 'manage_departments',
  // Create and staff the teams inside a department. Held by team leads as well
  // as admins; a lead may only touch teams in their own department.
  MANAGE_TEAMS: 'manage_teams',
  MANAGE_USERS: 'manage_users',
  VIEW_USERS: 'view_users',
  VIEW_AUDIT_LOG: 'view_audit_log',
  MANAGE_SETTINGS: 'manage_settings',
  /**
   * Running the call centre, as opposed to configuring it.
   *
   * Held by team leads as well as admins. Classifying an agent name, attaching a
   * SpoonFeed account, setting eligibility and moving a roster are the floor's
   * work; the connect thresholds and the sync jobs stay `MANAGE_SETTINGS`.
   */
  MANAGE_CALL_CENTRE: 'manage_call_centre',
  /** Named agent comparisons, planning, capacity, forecast and rosters. Enforced on the API too. */
  VIEW_CALL_CENTRE_TEAM: 'view_call_centre_team',
  /** The desk — live board, performance, lead lists, set-up. Agents do not hold it. Enforced on the API too. */
  VIEW_CALL_CENTRE_DESK: 'view_call_centre_desk',
  MANAGE_CLIENTS: 'manage_clients',
  // Change a location's own details. Narrower than MANAGE_CLIENTS (which also
  // creates, deletes and bulk-imports) and held by team leads as well as admins.
  EDIT_CLIENTS: 'edit_clients',
  VIEW_CLIENTS: 'view_clients',
  VIEW_OWN_CLIENTS: 'view_own_clients', // read-only, team member's own department locations
  VIEW_PENDING_WORK: 'view_pending_work',
  MANAGE_PENDING_WORK: 'manage_pending_work',
  VIEW_WORKFLOWS: 'view_workflows',
  MANAGE_WORKFLOWS: 'manage_workflows',
  VIEW_ONBOARDING: 'view_onboarding',
  MANAGE_ONBOARDING: 'manage_onboarding',
  ASSIGN_ONBOARDING: 'assign_onboarding',
  VIEW_ONBOARDING_CREDENTIALS: 'view_onboarding_credentials',
  GENERATE_REPORTS: 'generate_reports',
  VIEW_REPORTS: 'view_reports',
  APPROVE_TASKS: 'approve_tasks',
  DISTRIBUTE_REPORTS: 'distribute_reports',
  VIEW_ALL_TASKS: 'view_all_tasks',
  VIEW_OWN_TASKS: 'view_own_tasks',
  CREATE_TASKS: 'create_tasks',
  UPDATE_TASK_STATUS: 'update_task_status',
  /**
   * Remove a task from the app — admin by default, grantable from Role Access.
   *
   * Separate from `CREATE_TASKS`, which every team member holds so they can
   * write down their own work: "can file a task" is not "can make anyone's task
   * disappear". Hiding the button is a courtesy — the server checks too.
   */
  DELETE_TASKS: 'delete_tasks',
  VIEW_GLOBAL_DASHBOARD: 'view_global_dashboard',
  VIEW_CLIENT_DASHBOARD: 'view_client_dashboard',

  // Blog & Content Generation (Tools section)
  APPROVE_BLOG:  'approve_blog',

  /**
   * ── Platform administration ───────────────────────────────────────────────
   *
   * Mirrors the backend's `config/roleScopes.js`. The two lists had ALREADY
   * drifted before these were added — `manage_support_assignments` existed only
   * on the backend — so `nav-route-drift.test.js`'s sibling,
   * `permission-mirror-drift.test.js`, now fails when they disagree.
   */
  MANAGE_INTEGRATIONS: 'manage_integrations',
  MANAGE_API_KEYS: 'manage_api_keys',
  VIEW_AI_USAGE: 'view_ai_usage',
  MANAGE_ACCESS_CONTROL: 'manage_access_control',
  MANAGE_AUTO_GENERATION: 'manage_auto_generation',
  MANAGE_ONBOARDING_LIFECYCLE: 'manage_onboarding_lifecycle',
  VIEW_ACTIVITY: 'view_activity',
  VIEW_REPORT_CARD: 'view_report_card',
  MANAGE_CONTENT_REQUESTS: 'manage_content_requests',

  MANAGE_LEAD_VOCABULARY: 'manage_lead_vocabulary',
  MANAGE_NEWS_SOURCES: 'manage_news_sources',
  TRIAGE_TASKS: 'triage_tasks',

  // ── Escalation ladder ─────────────────────────────────────────────────────
  MANAGE_ESCALATION_POLICY: 'manage_escalation_policy',
  VIEW_ESCALATION: 'view_escalation',

  // ── Support Center ────────────────────────────────────────────────────────
  MANAGE_SUPPORT_ASSIGNMENTS: 'manage_support_assignments',
  TRIAGE_SUPPORT_TICKETS: 'triage_support_tickets',
  VIEW_SUPPORT_ADMIN: 'view_support_admin',
  MANAGE_SUPPORT_CONFIG: 'manage_support_config',
  DELETE_SUPPORT_TICKETS: 'delete_support_tickets',
};

// Which target roles each actor role may create (mirror of ROLE_CREATION_MATRIX).
export const ROLE_CREATION_MATRIX = {
  admin: ['admin', 'manager', 'team_member'],
  manager: ['team_member'],
  team_member: [],
};

// `user.permissions` is sent by the backend on login/me — prefer it.
export const hasPermission = (user, permission, requireAll = false) => {
  if (!user) return false;
  if (user.role === ROLES.ADMIN) return true;
  const perms = user.permissions || [];
  if (Array.isArray(permission)) {
    return requireAll ? permission.every((p) => perms.includes(p)) : permission.some((p) => perms.includes(p));
  }
  return perms.includes(permission);
};

export const canManageRole = (actorRole, targetRole) =>
  (ROLE_CREATION_MATRIX[actorRole] || []).includes(targetRole);

/** Team leaders may assign owners for their department's checklist; admins for any. */
/**
 * Admins may assign any department's checklist; a team lead only their own.
 *
 * ── The ordering is the fix ─────────────────────────────────────────────────
 *
 * This opened with `if (hasPermission(user, MANAGE_ONBOARDING)) return true`, and team
 * leads HOLD that permission — so it returned true for every pack and the department
 * check below it was dead code for exactly the role it was written for. The Assign
 * button therefore appeared on every department's checklist, and clicking it opened the
 * picker, which called the API, which correctly refused with "Team leaders can only
 * assign checklists for their own department" and left the list empty — reading as
 * "there is nobody to assign" rather than "this is not yours to assign".
 *
 * Mirrors `lib/assignments/service.js#authorizeAdapter`: admin passes outright, a
 * MANAGER is department-scoped. Keep the two in step — this one only decides whether to
 * render the button; the server decides whether the action happens.
 */
export function canAssignOnboardingPack(user, pack) {
  if (!user || !pack) return false;
  if (user.role === ROLES.ADMIN) return true;

  const mayAssign = hasPermission(user, PERMISSIONS.ASSIGN_ONBOARDING)
    || hasPermission(user, PERMISSIONS.MANAGE_ONBOARDING);
  if (!mayAssign) return false;

  if (user.role === ROLES.MANAGER) {
    const userDept = String(user.departmentId?._id || user.departmentId || '');
    const packDept = String(pack.departmentId?._id || pack.departmentId || '');
    return Boolean(userDept && packDept && userDept === packDept);
  }
  return false;
}
